import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { formatDate, accountHolderLabel } from '@/lib/utils';
import { lookupIbanInfo } from '@/lib/iban';
import { formatApiError } from '@/lib/errors';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DateInput from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const maskIban = (iban) => {
  if (!iban) return '–';
  const clean = String(iban).replace(/\s/g, '');
  if (clean.length <= 8) return clean;
  return `${clean.slice(0, 4)} •••• •••• ${clean.slice(-4)}`;
};

const fmtDate = (d) => (d ? formatDate(d) || '–' : '–');

function todayIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function isoDay(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

const ZAHLUNGSTYPEN = [
  { value: 'sepa', label: 'SEPA-Lastschrift' },
  { value: 'transfer', label: 'Selbstzahler / Überweiser' },
];

function zahlungsterminLabel(day) {
  return Number(day) === 15 ? 'zum 15.' : 'zum 1.';
}

function zahlungstypLabel(code) {
  const hit = ZAHLUNGSTYPEN.find((row) => row.value === (code || 'sepa'));
  return hit ? hit.label : 'SEPA-Lastschrift';
}

function hatBankdaten(row) {
  return Boolean(
    row?.bank_iban || row?.bank_name || row?.bank_bic
    || row?.sepa_mandate_reference || row?.bank_account_holder,
  );
}

function leerEntwurf(basis) {
  const src = basis || {};
  return {
    valid_from: todayIso(),
    bank_account_holder: src.bank_account_holder || '',
    bank_name: src.bank_name || '',
    bank_iban: src.bank_iban || '',
    bank_bic: src.bank_bic || '',
    sepa_mandate_reference: src.sepa_mandate_reference || '',
    sepa_mandate_date: isoDay(src.sepa_mandate_date),
    sepa_debit_day: src.sepa_debit_day ? String(src.sepa_debit_day) : '',
    payment_type: src.payment_type || 'sepa',
    sepa_debit_blocked: !!src.sepa_debit_blocked,
  };
}

function Feld({ label, children }) {
  return (
    <label className="block text-xs font-medium text-slate-600">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

/**
 * Bank-/SEPA-Verbindung als Historie: eine Änderung gilt ab einem Datum,
 * der bisherige Stand bleibt mit seinem Zeitraum sichtbar.
 */
export default function BankPeriodHistory({ guardian, onSaved }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIban, setShowIban] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!guardian?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/guardians/${guardian.id}/bank-periods`);
      const rows = Array.isArray(r.data) ? r.data : [];
      rows.sort((a, b) => String(b.valid_from || '').localeCompare(String(a.valid_from || '')));
      setPeriods(rows);
    } catch (error) {
      toast.error(formatApiError(error, 'Bankhistorie konnte nicht geladen werden'));
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  }, [guardian?.id]);

  useEffect(() => { load(); }, [load]);

  const aktuell = periods.find((row) => !row.valid_until) || periods[0] || null;

  const oeffnen = (basis) => {
    setDraft(leerEntwurf(basis || aktuell));
    setFormOpen(true);
  };

  const setFeld = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const ibanAendern = (value) => {
    setFeld('bank_iban', value);
    const cleaned = String(value || '').replace(/[\s-]/g, '');
    if (cleaned.length < 15) return;
    lookupIbanInfo(cleaned).then((info) => {
      if (!info) return;
      setDraft((prev) => ({
        ...prev,
        bank_iban: value,
        bank_bic: prev.bank_bic || info.bic || '',
        bank_name: prev.bank_name || info.bank_name || '',
      }));
    });
  };

  const speichern = async () => {
    if (!draft?.valid_from || saving) return;
    setSaving(true);
    const iban = (draft.bank_iban || '').replace(/[\s-]/g, '').toUpperCase();
    const mandate = isoDay(draft.sepa_mandate_date);
    try {
      await api.post(`/guardians/${guardian.id}/bank-periods`, {
        valid_from: draft.valid_from,
        bank_account_holder: draft.bank_account_holder || null,
        bank_name: draft.bank_name || null,
        bank_iban: iban || null,
        bank_bic: (draft.bank_bic || '').replace(/\s/g, '').toUpperCase() || null,
        sepa_mandate_reference: draft.sepa_mandate_reference || null,
        sepa_mandate_date: mandate ? new Date(`${mandate}T12:00:00`).toISOString() : null,
        sepa_debit_day: draft.sepa_debit_day ? Number(draft.sepa_debit_day) : null,
        payment_type: draft.payment_type || 'sepa',
        sepa_debit_blocked: !!draft.sepa_debit_blocked,
      });
      toast.success('Bankverbindung gespeichert. Sie gilt ab dem eingetragenen Datum.');
      setFormOpen(false);
      await load();
      if (onSaved) await onSaved();
    } catch (error) {
      toast.error(formatApiError(error, 'Bankverbindung konnte nicht gespeichert werden'));
    } finally {
      setSaving(false);
    }
  };

  const sichtbar = periods.filter(hatBankdaten);
  const einzugstage = ['1', '15'];
  if (draft?.sepa_debit_day && !einzugstage.includes(String(draft.sepa_debit_day))) {
    einzugstage.unshift(String(draft.sepa_debit_day));
  }

  return (
    <div data-testid="payer-accounts-bank-history">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">SEPA-Mandat</div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Eine Änderung gilt ab dem eingetragenen Datum. Die bisherige Bankverbindung bleibt mit ihrem Zeitraum in der Historie.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setShowIban((v) => !v)}
            className="text-slate-400 hover:text-slate-700"
            title={showIban ? 'IBAN verbergen' : 'IBAN anzeigen'}
            data-testid="payer-accounts-bank-iban-toggle"
          >
            {showIban ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          <Button type="button" size="sm" onClick={() => oeffnen(aktuell)} data-testid="payer-accounts-bank-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Neue Bankverbindung
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
      ) : sichtbar.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">Keine Bankverbindung hinterlegt</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-1.5 pr-3 font-medium">von</th>
                <th className="py-1.5 pr-3 font-medium">bis</th>
                <th className="py-1.5 pr-3 font-medium">IBAN</th>
                <th className="py-1.5 pr-3 font-medium">Bank</th>
                <th className="py-1.5 pr-3 font-medium">Kontoinhaber</th>
                <th className="py-1.5 pr-3 font-medium">Mandat</th>
                <th className="py-1.5 pr-3 font-medium">Zahlungstyp</th>
                <th className="py-1.5 pr-3 font-medium">Zahlungstermin</th>
                <th className="py-1.5 pr-3 font-medium">Sperre</th>
                <th className="py-1.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((row, index) => (
                <tr
                  key={row.id || `${row.valid_from}-${index}`}
                  className="border-b border-slate-100 last:border-0"
                  data-testid={`payer-accounts-bank-row-${row.id || index}`}
                >
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-900">{fmtDate(row.valid_from)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-700">{row.valid_until ? fmtDate(row.valid_until) : 'offen'}</td>
                  <td className="py-2 pr-3 font-mono text-slate-900 whitespace-nowrap">{showIban ? (row.bank_iban || '–') : maskIban(row.bank_iban)}</td>
                  <td className="py-2 pr-3 text-slate-900">{row.bank_name || '–'}</td>
                  <td className="py-2 pr-3 text-slate-700">{accountHolderLabel(row.bank_account_holder) === '—' ? '–' : accountHolderLabel(row.bank_account_holder)}</td>
                  <td className="py-2 pr-3 text-slate-700">{row.sepa_mandate_reference || '–'}</td>
                  <td className="py-2 pr-3 text-slate-700">{zahlungstypLabel(row.payment_type)}</td>
                  <td className="py-2 pr-3 text-slate-700">{zahlungsterminLabel(row.sepa_debit_day)}</td>
                  <td className="py-2 pr-3 text-slate-700">{row.sepa_debit_blocked ? 'gesperrt' : '–'}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => oeffnen(row)}
                      className="text-slate-400 hover:text-slate-800"
                      title="Ab diesem Stand ändern"
                      data-testid={`payer-accounts-bank-edit-${row.id || index}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="payer-accounts-bank-form"
          onInteractOutside={(e) => {
            const t = e.target;
            if (t instanceof Element && t.closest('[role="listbox"], [data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Bankverbindung ändern</DialogTitle>
            <DialogDescription>
              Die neue Verbindung gilt ab dem Datum. Der bisherige Stand bleibt bis zum Vortag in der Historie.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Feld label="Zahlungstyp">
                  <select
                    value={draft.payment_type || 'sepa'}
                    onChange={(e) => setFeld('payment_type', e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                    data-testid="payer-accounts-bank-payment-type"
                  >
                    {ZAHLUNGSTYPEN.map((art) => (
                      <option key={art.value} value={art.value}>{art.label}</option>
                    ))}
                  </select>
                </Feld>
              </div>
              <div className="col-span-2">
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!draft.sepa_debit_blocked}
                    onChange={(e) => setFeld('sepa_debit_blocked', e.target.checked)}
                    data-testid="payer-accounts-bank-debit-block"
                  />
                  <span>
                    <span className="font-medium">Lastschriftsperre</span>
                    <span className="block text-xs text-slate-500">Gesperrte Konten werden nicht per Lastschrift eingezogen, auch bei Mandat und Typ Lastschrift.</span>
                  </span>
                </label>
              </div>
              <Feld label="Gültig ab">
                <DateInput
                  value={draft.valid_from}
                  onChange={(e) => setFeld('valid_from', e.target.value)}
                  className="h-9 text-sm"
                  data-testid="payer-accounts-bank-valid-from"
                />
              </Feld>
              <Feld label="Zahlungstermin">
                <select
                  value={draft.sepa_debit_day || '1'}
                  onChange={(e) => setFeld('sepa_debit_day', e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  data-testid="payer-accounts-bank-debit-day"
                >
                  {einzugstage.map((day) => (
                    <option key={day} value={day}>
                      {day === '15' ? 'zum 15. des Monats' : day === '1' ? 'zum 1. des Monats' : `${day}. des Monats`}
                    </option>
                  ))}
                </select>
                <span className="block text-xs text-slate-500 mt-1">Lastschriftläufe zum 1. und zum 15. ziehen nur Konten mit diesem Termin ein. Ohne Angabe gilt der 1.</span>
              </Feld>
              <div className="col-span-2">
                <Feld label="Kontoinhaber">
                  <Input
                    value={draft.bank_account_holder}
                    onChange={(e) => setFeld('bank_account_holder', e.target.value)}
                    data-testid="payer-accounts-bank-holder-input"
                  />
                </Feld>
              </div>
              <div className="col-span-2">
                <Feld label="IBAN">
                  <Input
                    value={draft.bank_iban}
                    onChange={(e) => ibanAendern(e.target.value)}
                    className="font-mono"
                    data-testid="payer-accounts-bank-iban-input"
                  />
                </Feld>
              </div>
              <Feld label="BIC">
                <Input
                  value={draft.bank_bic}
                  onChange={(e) => setFeld('bank_bic', e.target.value)}
                  data-testid="payer-accounts-bank-bic-input"
                />
              </Feld>
              <Feld label="Bank">
                <Input
                  value={draft.bank_name}
                  onChange={(e) => setFeld('bank_name', e.target.value)}
                  data-testid="payer-accounts-bank-name-input"
                />
              </Feld>
              <Feld label="Mandatsreferenz">
                <Input
                  value={draft.sepa_mandate_reference}
                  onChange={(e) => setFeld('sepa_mandate_reference', e.target.value)}
                  data-testid="payer-accounts-bank-mandate-input"
                />
              </Feld>
              <Feld label="Mandat erteilt am">
                <DateInput
                  value={draft.sepa_mandate_date}
                  onChange={(e) => setFeld('sepa_mandate_date', e.target.value)}
                  className="h-9 text-sm"
                  data-testid="payer-accounts-bank-mandate-date"
                />
              </Feld>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Abbrechen</Button>
            <Button
              type="button"
              onClick={speichern}
              disabled={!draft?.valid_from || saving}
              data-testid="payer-accounts-bank-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
